"use client";

import { MapPin } from "@/components/icons";
import { SimpleZipInput } from "@/components/inputs/simple-zip-input";
import { useHeroMapContext } from "@/components/home/context/hero-map-context";
import { useFieldConfig } from "@/components/specs/hooks/use-field-config";
import {
  GroupedListItem,
  GroupedListRow,
  GroupedListSection,
} from "@/components/ui/grouped-list";
import { PRODUCT_TYPE_FIELDS } from "@/lib/generated/product-type-fields";
import { isValidProductTypeSlug } from "@shared/generated/productConditions";
import { getProductTypeBySlug } from "@/lib/generated/product-types";
import { useListingBrowseContext } from "../context/listing-browse-context";
import { LISTING_INTENT_OPTIONS } from "../context/listing-browse-types";
import { PRODUCT_TYPE_OPTIONS, useSelectProductType } from "./primary-filters";
import { RADIUS_PRESETS, useLocationHandlers } from "./location-filter";
import {
  ALL_PRODUCTS_CONDITION_OPTIONS,
  LISTED_AFTER_OPTIONS,
  STATE_OPTIONS,
} from "./secondary-filters";
import { PhoneNumberRow, PhoneSelectRow } from "./phone-filter-rows";

const PRODUCT_TYPE_SELECT_OPTIONS = PRODUCT_TYPE_OPTIONS.map((type) => ({
  label: type.pluralDisplayName,
  value: type.slug,
}));

export function PhoneProductSection() {
  const { productType, setProductType, lockedProductType } =
    useListingBrowseContext();
  const selectProductType = useSelectProductType();
  if (lockedProductType) return null;
  return (
    <GroupedListSection>
      <PhoneSelectRow
        label="Product"
        value={productType ?? ""}
        options={PRODUCT_TYPE_SELECT_OPTIONS}
        placeholder="All products"
        onChange={(next) =>
          next ? selectProductType(next) : setProductType(null)
        }
      />
    </GroupedListSection>
  );
}

function PhoneStateRow() {
  const { stateCode, setStateCode } = useListingBrowseContext();
  const { setLocation } = useHeroMapContext();
  return (
    <PhoneSelectRow
      label="Or a whole state"
      value={stateCode ?? ""}
      options={STATE_OPTIONS}
      placeholder="Any state"
      onChange={(next) => {
        setStateCode(next || null);
        if (next) setLocation(null);
      }}
    />
  );
}

export function PhoneLocationSection() {
  const heroMap = useHeroMapContext();
  const { handleZipSelect, handleUseMyLocation, handleClear } =
    useLocationHandlers();
  const location = heroMap.location;
  const radiusMiles = heroMap.radiusMiles ?? 50;
  const cityLine = location?.city ? `${location.city}, ${location.state}` : null;
  return (
    <GroupedListSection title="Location">
      <GroupedListItem className="gap-2 py-1">
        <span className="shrink-0 text-foreground">ZIP code</span>
        <SimpleZipInput
          value={location?.postalCode ?? ""}
          onChange={() => {}}
          onZipSelect={handleZipSelect}
          placeholder="Enter ZIP"
          showValidIndicator={false}
          className="h-11 min-w-0 flex-1 border-0 bg-transparent px-0 text-right text-base text-foreground placeholder:text-fg-subtle focus-visible:outline-none focus-visible:ring-0"
        />
      </GroupedListItem>
      {cityLine ? (
        <GroupedListRow onSelect={handleClear} trailing={<span className="text-sm text-action">Clear</span>}>
          {cityLine}
        </GroupedListRow>
      ) : null}
      <GroupedListRow onSelect={handleUseMyLocation} icon={MapPin}>
        Use my location
      </GroupedListRow>
      {location?.lat !== undefined ? (
        <PhoneSelectRow
          label="Within"
          value={String(radiusMiles)}
          options={RADIUS_PRESETS.map((preset) => ({
            label: preset.label,
            value: String(preset.value),
          }))}
          placeholder="50 mi"
          onChange={(next) => heroMap.setRadiusMiles(Number(next || 50))}
        />
      ) : null}
      <PhoneStateRow />
    </GroupedListSection>
  );
}

export function PhoneListingSection() {
  const { intentFilter, selectIntentFilter, saleOnly } =
    useListingBrowseContext();
  return (
    <GroupedListSection title="Listing">
      {saleOnly ? null : (
        <PhoneSelectRow
          label="How to buy"
          value={intentFilter}
          options={LISTING_INTENT_OPTIONS}
          placeholder="Any"
          onChange={(next) =>
            selectIntentFilter(next === "recycle" ? "recycle" : "all")
          }
        />
      )}
      <PhonePriceRows />
      <PhoneListingDetailRows />
    </GroupedListSection>
  );
}

function PhonePriceRows() {
  const { intentFilter, priceRange, setPriceRange } = useListingBrowseContext();
  if (intentFilter === "recycle") return null;
  return (
    <>
      <PhoneNumberRow
        label="Min price"
        prefix="$"
        placeholder="Any"
        value={priceRange.min}
        onChange={(min) => setPriceRange({ min, max: priceRange.max })}
      />
      <PhoneNumberRow
        label="Max price"
        prefix="$"
        placeholder="Any"
        value={priceRange.max}
        onChange={(max) => setPriceRange({ min: priceRange.min, max })}
      />
    </>
  );
}

function PhoneListingDetailRows() {
  const { listedAfterDays, setListedAfterDays } = useListingBrowseContext();
  const { quantity, setQuantity } = useHeroMapContext();
  return (
    <>
      <PhoneSelectRow
        label="Listed within"
        value={listedAfterDays !== null ? String(listedAfterDays) : ""}
        options={LISTED_AFTER_OPTIONS}
        placeholder="Any time"
        onChange={(next) => setListedAfterDays(next ? Number(next) : null)}
      />
      <PhoneNumberRow
        label="Quantity needed"
        placeholder="1"
        min={1}
        value={quantity > 1 ? quantity : null}
        onChange={(next) => setQuantity(Math.max(1, next ?? 1))}
      />
    </>
  );
}

export function PhoneConditionSection() {
  const { conditions, setConditions, productType } = useListingBrowseContext();
  const { getConditionOptions } = useFieldConfig(productType || "");
  const options =
    productType === null
      ? [...ALL_PRODUCTS_CONDITION_OPTIONS]
      : getConditionOptions();
  if (options.length === 0) return null;
  return (
    <GroupedListSection title="Condition">
      {options.map((option) => (
        <GroupedListRow
          key={option.value}
          checked={conditions.includes(option.value)}
          onSelect={() =>
            setConditions(
              conditions.includes(option.value)
                ? conditions.filter((entry) => entry !== option.value)
                : [...conditions, option.value],
            )
          }
        >
          {option.label}
        </GroupedListRow>
      ))}
    </GroupedListSection>
  );
}

export function PhoneSpecSection() {
  const { productType, specFilters, addSpecFilter, removeSpecFilter } =
    useListingBrowseContext();
  const fields =
    productType && isValidProductTypeSlug(productType)
      ? PRODUCT_TYPE_FIELDS[productType]?.filter(
          (field) => field.type === "select" && field.name !== "condition",
        )
      : null;
  if (!productType || !fields || fields.length === 0) return null;
  const typeName = getProductTypeBySlug(productType)?.displayName ?? "Specs";
  return (
    <GroupedListSection title={`${typeName} specs`}>
      {fields.map((field) => (
        <PhoneSelectRow
          key={field.name}
          label={field.label}
          value={specFilters.find((entry) => entry.field === field.name)?.value ?? ""}
          options={field.options ?? []}
          placeholder="Any"
          onChange={(next) =>
            next
              ? addSpecFilter({ field: field.name, value: next })
              : removeSpecFilter(field.name)
          }
        />
      ))}
    </GroupedListSection>
  );
}
